import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import { Link } from 'react-router-dom'
import { isAnyElementInArray } from '../../utils/arrayUtil'
import {
  getGroupQrTags,
  getQrTagLatestEvent,
} from '../../modules/QrTag'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { fetchGroupEventTypes } from '../../modules/QrTagEventTypes'
import QrTagsListItem from './QrTagsListItem'
import DetailQrTagModal from './DetailQrTagModal'
import QrTagEventScannerModal from './QrTagEventScannerModal'
import './OverviewItemList.css'
// import { GROUP_QR_PRINT, QR_TAG_EVENT_RECORDER } from '../../constants/Paths'
import { GROUP_QR_PRINT } from '../../constants/Paths'

type Props = {
  group: IGroup
  keyword?: string
}

const QrTagsList = ({
  group,
  keyword = '',
}: Props) => {
  const dispatch = useAppDispatch()
  const eventTypesEntry = useAppSelector((state) => state.qrTagEventTypes.entries[group.id])
  const eventTypes = useMemo(() => eventTypesEntry?.values ?? [], [eventTypesEntry])
  const [qrTags, setQrTags] = useState<IQrTag[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedTag, setSelectedTag] = useState<IQrTag | null>(null)
  const [isScannerOpen, setIsScannerOpen] = useState(false)
  const isMountedRef = useRef(true)

  useEffect(() => () => {
    isMountedRef.current = false
  }, [])

  useEffect(() => {
    dispatch(fetchGroupEventTypes({ groupId: group.id }))
  }, [dispatch, group.id])

  const attachLatestEvent = useCallback(async (tags: IQrTag[]): Promise<IQrTag[]> => {
    const results = await Promise.all(tags.map(async (tag) => {
      try {
        const lastEvent = await getQrTagLatestEvent(group.id, tag.id)
        return {
          ...tag,
          last_event: lastEvent,
        }
      } catch (err) {
        console.error('Failed to fetch latest event for QR tag', tag.id, err)
        return {
          ...tag,
          last_event: null,
        }
      }
    }))

    return results
  }, [group.id])

  const fetchQrTags = useCallback(async () => {
    if (!isMountedRef.current) return

    try {
      setLoading(true)
      const tags = await getGroupQrTags(group.id, keyword)
      const hydratedTags = await attachLatestEvent(tags)
      if (!isMountedRef.current) return
      setQrTags(hydratedTags)
      setError(null)
    } catch (err) {
      if (!isMountedRef.current) return
      console.error('Failed to load QR tags', err)
      setError('QRタグの読み込みに失敗しました')
      setQrTags([])
    } finally {
      if (isMountedRef.current) {
        setLoading(false)
      }
    }
  }, [attachLatestEvent, group.id, keyword])

  useEffect(() => {
    fetchQrTags()
  }, [fetchQrTags])

  const emptyRowText = useMemo(() => {
    if (loading) return 'QRタグを読み込み中...'
    if (error) return error
    return 'このグループにはQRタグがありません。'
  }, [loading, error])

  // const recorderPagePath = useMemo(
  //   () => QR_TAG_EVENT_RECORDER.replace(':group_id', String(group.id)),
  //   [group.id],
  // )

  const groupQrPrintPagePath = useMemo(
    () => GROUP_QR_PRINT.replace(':group_id', String(group.id)),
    [group.id],
  )

  const eventTypeColors = useMemo(() => (
    new Map(eventTypes.map((eventType) => [eventType.name, eventType.color]))
  ), [eventTypes])

  const handleScanCompleted = async () => {
    setIsScannerOpen(false)
    await fetchQrTags()
  }

  return (
    <div className="overview-list" data-testid="qr-tags-list">
      <div style={{
        display: 'flex', justifyContent: 'flex-end', marginBottom: '8px', marginTop: '24px', gap: '8px',
      }}
      >
        {/* <button
          type="button"
          className="btn btn-primary"
          onClick={() => setIsScannerOpen(true)}
          data-testid="qr-tag-open-scanner-button"
        >
          QRタグイベントを記録する
        </button> */}
        <Link
          to={groupQrPrintPagePath}
          className="btn btn-outline-secondary"
        >
          グループQRを印刷する
        </Link>
        {/* <Link
          to={recorderPagePath}
          className="btn btn-outline-secondary hidden"
        >
          専用ページを開く
        </Link> */}
      </div>
      <table className="table">
        <thead className="header">
          <tr>
            <th><div className="title">タグ名</div></th>
            <th><div className="title">説明</div></th>
            <th><div className="title">QRコード番号</div></th>
            <th><div className="title">最新イベント</div></th>
            <th><div className="title">時刻</div></th>
          </tr>
        </thead>
        <tbody className="list">
          {
            !qrTags || !isAnyElementInArray(qrTags)
              ? (
                <tr>
                  <td colSpan={5} className="list-row">{emptyRowText}</td>
                </tr>
              )
              : qrTags.map((tag) => (
                <QrTagsListItem
                  key={tag.id}
                  qrTag={tag}
                  onClick={() => setSelectedTag(tag)}
                  eventTypeColors={eventTypeColors}
                />
              ))
          }
        </tbody>
      </table>
      {selectedTag && (
        <DetailQrTagModal
          modalProps={{
            qrTag: selectedTag,
            group,
            refreshList: fetchQrTags,
          }}
          closeModalFunction={() => setSelectedTag(null)}
        />
      )}
      {isScannerOpen && (
        <QrTagEventScannerModal
          modalProps={{
            group,
            onCompleted: handleScanCompleted,
          }}
          closeModalFunction={() => setIsScannerOpen(false)}
        />
      )}
    </div>
  )
}

export default QrTagsList
